import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, TrendingUp, Building2, ArrowRight, Home, Store, Factory, Sparkles, IndianRupee, BookOpen } from "lucide-react";
import api from "@/lib/api";
import PropertyCard from "@/components/site/PropertyCard";
import { LOCALITIES } from "@/data/constants";
import { Button } from "@/components/ui/button";
import { BarChart } from "@/components/site/Charts";

const HERO_BY_LOCALITY = {
  default: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=1920&q=80",
  Vesu: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1920&q=80",
  Adajan: "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=1920&q=80",
  Piplod: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=1920&q=80",
  Pandesara: "https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=1920&q=80",
  Sachin: "https://images.unsplash.com/photo-1587293852726-70cdb56c2866?auto=format&fit=crop&w=1920&q=80",
};

function slugToName(slug) {
  const found = LOCALITIES.find((l) => l.toLowerCase().replace(/\s+/g, "-") === slug.toLowerCase());
  return found || slug.split("-").map((w) => w[0]?.toUpperCase() + w.slice(1)).join(" ");
}

const inr = (n) => n ? `\u20b9${Math.round(n).toLocaleString("en-IN")}` : "\u2014";
const inrShort = (n) => {
  if (!n) return "\u2014";
  if (n >= 10000000) return `\u20b9${(n/10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `\u20b9${(n/100000).toFixed(1)} L`;
  return `\u20b9${n.toLocaleString("en-IN")}`;
};

export default function Locality({ onLoginClick }) {
  const { name } = useParams();
  const displayName = slugToName(name);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purpose, setPurpose] = useState("all");

  useEffect(() => {
    document.title = `Property in ${displayName}, Surat — Buy, Rent & Lease | Krishna Property`;
    const desc = `Explore verified properties for sale, rent and lease in ${displayName}, Surat. Market trends, average prices, and expert insights from Krishna Property.`;
    let meta = document.querySelector("meta[name='description']");
    if (meta) meta.setAttribute("content", desc);
    return () => { document.title = "Krishna Property — Buy, Sell, Rent & Lease in Surat"; };
  }, [displayName]);

  useEffect(() => {
    setLoading(true);
    const params = { locality: displayName };
    if (purpose !== "all") params.purpose = purpose === "rent" ? "rent,lease" : purpose;
    Promise.all([
      api.get("/properties", { params }),
      api.get(`/localities/${encodeURIComponent(displayName)}/stats`),
      api.get("/blog", { params: { locality: displayName, limit: 3 } }),
    ]).then(([p, s, b]) => {
      setItems(p.data);
      setStats(s.data);
      setPosts(b.data);
    }).finally(() => setLoading(false));
  }, [displayName, purpose]);

  const counts = {
    all: items.length,
    sale: items.filter((p) => p.purpose === "sale").length,
    rent: items.filter((p) => p.purpose === "rent" || p.purpose === "lease").length,
  };
  const catCounts = ["residential", "commercial", "industrial", "warehouse", "plot"].reduce((acc, c) => ({ ...acc, [c]: items.filter((p) => p.category === c).length }), {});
  const hero = HERO_BY_LOCALITY[displayName] || HERO_BY_LOCALITY.default;

  return (
    <div>
      <section className="relative">
        <img src={hero} alt={`${displayName} Surat`} className="w-full h-72 object-cover"/>
        <div className="absolute inset-0 hero-overlay"/>
        <div className="absolute inset-0 max-w-[1400px] mx-auto px-4 flex flex-col justify-end pb-8">
          <div className="text-xs text-white/80 font-semibold uppercase tracking-widest flex items-center gap-1"><MapPin size={12}/> Surat, Gujarat</div>
          <h1 className="font-display text-white text-4xl sm:text-5xl font-bold mt-2">Property in <span className="bg-kp-maroon px-3 rounded-lg">{displayName}</span></h1>
          <p className="text-white/85 mt-2 max-w-2xl">Verified listings, live market trends, and expert insights on {displayName}, Surat.</p>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-4 py-8">
        {/* Market Trends */}
        {stats && (
          <section className="mb-10" data-testid="locality-market-trends">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-lg bg-kp-teal-light text-kp-teal flex items-center justify-center"><TrendingUp size={18}/></div>
              <div>
                <h2 className="font-display text-2xl font-bold text-slate-900">{displayName} Market Trends</h2>
                <p className="text-xs text-slate-500">Live data from Krishna Property listings.</p>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="bg-white border border-slate-200 rounded-2xl p-5">
                <div className="text-xs text-slate-500">Avg. Sale Price</div>
                <div className="font-display text-2xl font-bold text-slate-900 mt-1">{inrShort(stats.avg_sale_price)}</div>
                <div className="text-[11px] text-slate-500 mt-1">Based on {stats.by_purpose.sale} listing(s)</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-2xl p-5">
                <div className="text-xs text-slate-500">Price per Sq.Ft.</div>
                <div className="font-display text-2xl font-bold text-slate-900 mt-1">{inr(stats.avg_price_sqft)}</div>
                <div className="text-[11px] text-slate-500 mt-1">Residential + Commercial</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-2xl p-5">
                <div className="text-xs text-slate-500">Avg. Monthly Rent / Lease</div>
                <div className="font-display text-2xl font-bold text-slate-900 mt-1">{inrShort(stats.avg_rent)}</div>
                <div className="text-[11px] text-slate-500 mt-1">{stats.by_purpose.rent + stats.by_purpose.lease} rentals</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-2xl p-5">
                <div className="text-xs text-slate-500 flex items-center gap-1"><Sparkles size={12}/> New in Last 30 Days</div>
                <div className="font-display text-2xl font-bold text-kp-maroon mt-1">{stats.new_last_30d}</div>
                <div className="text-[11px] text-slate-500 mt-1">Fresh listings added</div>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-3 mt-4">
              <div className="bg-white border border-slate-200 rounded-2xl p-5 lg:col-span-2">
                <h3 className="font-display font-semibold text-slate-900 mb-2 flex items-center gap-1"><TrendingUp size={16}/> Listing Activity — Last 6 Months</h3>
                <BarChart data={stats.trend_6m} color="#0F5E5A"/>
              </div>
              <div className="bg-white border border-slate-200 rounded-2xl p-5">
                <h3 className="font-display font-semibold text-slate-900 mb-2">Popular Property Types</h3>
                {stats.popular_types.length === 0 ? (
                  <p className="text-slate-500 text-sm mt-4">Not enough data yet.</p>
                ) : (
                  <div className="space-y-2 mt-3">
                    {stats.popular_types.map((t, i) => (
                      <div key={t.type} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2"><span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? "bg-kp-maroon text-white" : "bg-slate-100 text-slate-700"}`}>{i + 1}</span> <span className="font-medium">{t.type}</span></div>
                        <span className="text-slate-500 text-xs">{t.count} listing(s)</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="w-11 h-11 rounded-lg bg-kp-teal-light text-kp-teal flex items-center justify-center mb-3"><Building2 size={20}/></div>
            <div className="font-display text-2xl font-bold text-slate-900">{counts.all}</div>
            <div className="text-xs text-slate-500">Total listings in {displayName}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="w-11 h-11 rounded-lg bg-kp-maroon-light text-kp-maroon flex items-center justify-center mb-3"><Home size={20}/></div>
            <div className="font-display text-2xl font-bold text-slate-900">{catCounts.residential}</div>
            <div className="text-xs text-slate-500">Residential</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="w-11 h-11 rounded-lg bg-kp-teal-light text-kp-teal flex items-center justify-center mb-3"><Store size={20}/></div>
            <div className="font-display text-2xl font-bold text-slate-900">{catCounts.commercial}</div>
            <div className="text-xs text-slate-500">Commercial</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="w-11 h-11 rounded-lg bg-kp-maroon-light text-kp-maroon flex items-center justify-center mb-3"><Factory size={20}/></div>
            <div className="font-display text-2xl font-bold text-slate-900">{catCounts.industrial + (catCounts.warehouse || 0)}</div>
            <div className="text-xs text-slate-500">Industrial &amp; Warehouse</div>
          </div>
        </div>

        <div className="flex gap-2 border-b border-slate-200 mb-6 overflow-x-auto no-scrollbar">
          {[["all","All"],["sale","Buy"],["rent","Rent / Lease"]].map(([k, l]) => (
            <button key={k} onClick={() => setPurpose(k)} className={`px-4 py-2 text-sm font-semibold border-b-2 whitespace-nowrap ${purpose === k ? "border-kp-teal text-kp-teal" : "border-transparent text-slate-600 hover:text-kp-teal"}`} data-testid={`locality-tab-${k}`}>
              {l} <span className="ml-1 text-xs bg-slate-100 rounded-full px-1.5">{counts[k]}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{Array.from({length:6}).map((_,i) => <div key={i} className="bg-slate-100 rounded-xl h-80 animate-pulse"/>)}</div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
            <p className="text-slate-600">No properties currently listed in {displayName}. Check nearby areas.</p>
            <Link to="/properties" className="inline-block mt-4"><Button className="btn-primary rounded-full">Browse All Properties</Button></Link>
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((p) => <PropertyCard key={p.id} property={p} onLoginRequired={onLoginClick}/>)}
          </div>
        )}

        {posts.length > 0 && (
          <section className="mt-14">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2"><BookOpen size={20} className="text-kp-teal"/> Blog: {displayName} Insights</h2>
              <Link to={`/blog?locality=${encodeURIComponent(displayName)}`} className="text-sm text-kp-teal font-semibold flex items-center gap-1">All Articles <ArrowRight size={14}/></Link>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              {posts.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-kp-teal hover:shadow-md transition-all">
                  <div className="text-[10px] uppercase tracking-widest text-kp-maroon font-bold">{p.tags?.[0] || "Insight"}</div>
                  <h3 className="font-display font-bold text-slate-900 mt-1 line-clamp-2">{p.title}</h3>
                  <p className="text-xs text-slate-500 mt-2 line-clamp-3">{p.excerpt}</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="mt-16 bg-white border border-slate-200 rounded-2xl p-6">
          <h2 className="font-display text-2xl font-bold text-slate-900">About {displayName}, Surat</h2>
          <p className="text-slate-600 mt-3 leading-relaxed">
            {displayName} is a well-connected locality in Surat, Gujarat, offering a healthy mix of residential apartments, commercial spaces, and industrial facilities.
            Buyers looking for property for sale in {displayName} can find everything from budget flats to luxury villas, while tenants can explore rent and lease listings across every price range.
            Krishna Property curates verified listings in {displayName} so you can transact with confidence.
          </p>
          <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
            <Link to={`/properties?purpose=sale&locality=${displayName}`} className="rounded-xl border border-slate-200 p-3 hover:border-kp-teal hover:text-kp-teal transition-colors">Property for Sale in {displayName} <ArrowRight size={12} className="inline ml-1"/></Link>
            <Link to={`/properties?purpose=rent,lease&locality=${displayName}`} className="rounded-xl border border-slate-200 p-3 hover:border-kp-teal hover:text-kp-teal transition-colors">Property for Rent in {displayName} <ArrowRight size={12} className="inline ml-1"/></Link>
            <Link to={`/properties?category=commercial&locality=${displayName}`} className="rounded-xl border border-slate-200 p-3 hover:border-kp-teal hover:text-kp-teal transition-colors">Commercial in {displayName} <ArrowRight size={12} className="inline ml-1"/></Link>
          </div>
        </section>

        <section className="mt-10">
          <h2 className="font-display text-2xl font-bold text-slate-900">Explore Other Surat Localities</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {LOCALITIES.filter((l) => l !== displayName).map((l) => (
              <Link key={l} to={`/locality/${l.toLowerCase().replace(/\s+/g, "-")}`} className="text-sm bg-kp-teal-light text-kp-teal hover:bg-kp-teal hover:text-white transition-colors px-4 py-2 rounded-full flex items-center gap-1">
                <MapPin size={12}/> {l}
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
